/**
 * /dashboard/accounting/audit — Audit Trail viewer (G_ACC9).
 *
 * Renders audit_events rows for the active tenant. Every state change
 * the accounting engine writes (post / void / lock / unlock / master_*)
 * lands in the table — the DB layer enforces immutability and 8-year
 * retention (Companies Act §128(5)).
 *
 * Statutory anchors:
 *   - Companies Act 2013 §128(1)  — true and fair view
 *   - Companies (Accounts) Amendment Rules 2021 Rule 3(1) — non-
 *     disableable audit trail of EVERY transaction
 *   - Companies Act 2013 §128(5)  — 8-year retention
 *
 * Filters: action, entity_type, actor_id, date range. Pagination is
 * offset-based (the backend caps page size at 100). The row is
 * collapsible to show the before/after JSONB snapshots.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/audit/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  History,
} from "lucide-react";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuditEvents } from "@/hooks/use-accounting";
import {
  AUDIT_EVENT_ACTIONS,
  AUDIT_EVENT_ACTION_LABELS,
  type AuditEvent,
  type AuditEventAction,
  type ListAuditEventsParams,
} from "@/types/accounting";
import { toApiError } from "@/lib/api";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/audit",
)({
  component: AuditTrailRoute,
});

const PAGE_SIZE = 50;
// Sentinel used as the Select value for "no filter" — Radix Select
// rejects empty-string values, so we use a literal that no real
// taxonomy entry will match.
const ANY = "__any__";

function formatRowDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatJSON(value: unknown): string {
  if (value === undefined || value === null) return "—";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function ActionBadge({ action }: { action: AuditEventAction }) {
  // Visual cue: void / unlock are negative, lock is neutral, master_archive
  // is destructive-leaning; everything else is positive.
  const variant: "default" | "secondary" | "destructive" | "outline" =
    action === "void" || action === "unlock"
      ? "destructive"
      : action === "lock"
        ? "secondary"
        : action === "master_archive"
          ? "outline"
          : "default";
  return (
    <Badge variant={variant} className="font-mono text-xs">
      {AUDIT_EVENT_ACTION_LABELS[action]}
    </Badge>
  );
}

function AuditRow({ event }: { event: AuditEvent }) {
  const [open, setOpen] = useState(false);
  const hasSnapshot =
    event.before !== undefined && event.before !== null
      ? true
      : event.after !== undefined && event.after !== null;
  return (
    <>
      <TableRow data-testid={`audit-row-${event.id}`}>
        <TableCell className="w-8 align-top">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setOpen((o) => !o)}
            disabled={!hasSnapshot}
            aria-label={open ? "Hide snapshot" : "Show snapshot"}
          >
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
        <TableCell className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
          {formatRowDate(event.at)}
        </TableCell>
        <TableCell>
          <ActionBadge action={event.action} />
        </TableCell>
        <TableCell>
          <div className="text-sm">{event.summary}</div>
          <div className="text-xs text-muted-foreground">
            {event.entity_type} #{event.entity_id}
          </div>
        </TableCell>
        <TableCell>
          {event.actor_name || `User #${event.actor_id}`}
          {event.actor_email && (
            <div className="text-xs text-muted-foreground">
              {event.actor_email}
            </div>
          )}
        </TableCell>
        <TableCell className="font-mono text-xs text-muted-foreground">
          {event.ip ?? "—"}
        </TableCell>
      </TableRow>
      {open && hasSnapshot && (
        <TableRow className="bg-muted/40">
          <TableCell colSpan={6} className="py-3">
            <div className="grid gap-3 lg:grid-cols-2">
              <div>
                <div className="mb-1 text-xs font-semibold text-muted-foreground">
                  Before
                </div>
                <pre className="max-h-96 overflow-auto rounded-md border bg-background p-2 text-xs">
                  {formatJSON(event.before)}
                </pre>
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold text-muted-foreground">
                  After
                </div>
                <pre className="max-h-96 overflow-auto rounded-md border bg-background p-2 text-xs">
                  {formatJSON(event.after)}
                </pre>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function AuditTrailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const [action, setAction] = useState<AuditEventAction | "">("");
  const [entityType, setEntityType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);

  // Snapshot of the applied filters so changing the inputs doesn't
  // refetch on every keystroke. "Apply" commits.
  const [applied, setApplied] = useState<ListAuditEventsParams>({
    limit: PAGE_SIZE,
    offset: 0,
  });

  const query = useAuditEvents(applied);
  const events = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const loading = query.isPending;
  const error = query.isError ? toApiError(query.error).message : null;

  function applyFilters(nextOffset = 0) {
    const next: ListAuditEventsParams = {
      limit: PAGE_SIZE,
      offset: nextOffset,
    };
    if (action) next.action = action;
    if (entityType.trim()) next.entity_type = entityType.trim();
    if (from) next.from = from;
    if (to) next.to = to;
    setApplied(next);
    setOffset(nextOffset);
  }

  function resetFilters() {
    setAction("");
    setEntityType("");
    setFrom("");
    setTo("");
    setApplied({ limit: PAGE_SIZE, offset: 0 });
    setOffset(0);
  }

  function prevPage() {
    if (offset === 0) return;
    applyFilters(Math.max(0, offset - PAGE_SIZE));
  }
  function nextPage() {
    if (offset + PAGE_SIZE >= total) return;
    applyFilters(offset + PAGE_SIZE);
  }

  return (
    <AccountingModuleShell
      title="Audit Trail"
      description="Every accounting state change. Rule 3(1) non-disableable; §128(5) 8-year retention."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/accounting/reports" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <Card className="mt-4">
        <CardContent className="grid gap-3 py-4 md:grid-cols-2 lg:grid-cols-5">
          <div className="grid gap-1.5">
            <Label htmlFor="ae-action">Action</Label>
            <Select
              value={action || ANY}
              onValueChange={(v) =>
                setAction(v === ANY ? "" : (v as AuditEventAction))
              }
            >
              <SelectTrigger id="ae-action">
                <SelectValue placeholder="Any action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any action</SelectItem>
                {AUDIT_EVENT_ACTIONS.map((a) => (
                  <SelectItem key={a} value={a}>
                    {AUDIT_EVENT_ACTION_LABELS[a]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ae-entity-type">Entity type</Label>
            <Input
              id="ae-entity-type"
              placeholder="e.g. journal_entry"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ae-from">From</Label>
            <Input
              id="ae-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="ae-to">To</Label>
            <Input
              id="ae-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="flex items-end gap-2">
            <Button type="button" onClick={() => applyFilters(0)}>
              Apply
            </Button>
            <Button type="button" variant="outline" onClick={resetFilters}>
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Could not load audit events</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="mt-4 rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="w-44">When</TableHead>
              <TableHead className="w-28">Action</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead className="w-56">Actor</TableHead>
              <TableHead className="w-32">IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-8" />
                  </TableCell>
                </TableRow>
              ))
            ) : events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center">
                  <History className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    No audit events match these filters.
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              events.map((ev) => <AuditRow key={ev.id} event={ev} />)
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
        <div>
          {total === 0
            ? "0 events"
            : `Showing ${offset + 1}–${Math.min(offset + events.length, total)} of ${total}`}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={prevPage}
            disabled={offset === 0 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={nextPage}
            disabled={offset + PAGE_SIZE >= total || loading}
          >
            Next
          </Button>
        </div>
      </div>
    </AccountingModuleShell>
  );
}
